import { Component, HostBinding, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CartridgeServicePackage, Price } from '@markant-platform/cartridge/model';
import { formatPriceWithCurrencyPipe, isBulkPrice, RegexPattern } from '@markant-platform/shared/util-helpers';
import { CurrencyPipe } from '@angular/common';
import { ServicePackageNamePipe } from '@markant-platform/shared/util-pipes';

@Component({
  selector: 'markant-platform-price',
  templateUrl: './price.component.html',
  styleUrls: ['./price.component.scss'],
})
export class PriceComponent implements OnChanges {
  @Input() price!: Price;
  @Input() servicePackage?: CartridgeServicePackage[];

  @Input()
  @HostBinding('class.compact')
  compact = false;

  readonly formatBulkPrice = formatPriceWithCurrencyPipe;

  isBulkPrice!: boolean;
  displayPrice!: string | null;
  displayPriceCurrency!: string;
  displayServicePackageName!: string | undefined;
  currencySymbolPosition!: 'TRAILING' | 'LEADING';

  constructor(
    private readonly formatter: CurrencyPipe,
    private readonly servicePackageName: ServicePackageNamePipe
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['price']) {
      this.isBulkPrice = isBulkPrice(this.price);
      const variantPrice = this.getPriceInformation();
      this.getDisplayPriceInformation(variantPrice ?? '');
    }
  }

  private getDisplayPriceInformation(price: string): void {
    this.displayPriceCurrency = price.match(RegexPattern.CURRENCY_SYMBOLS)?.[0] ?? '';
    this.currencySymbolPosition = price.startsWith(this.displayPriceCurrency) ? 'LEADING' : 'TRAILING';
    this.displayPrice = price.replace(this.displayPriceCurrency, '').trim();
  }

  private getPriceInformation(): string | null {
    if (!this.isBulkPrice) {
      return this.formatPrice(this.price.centAmount, this.price.currency, this.formatter);
    }

    if (!this.price.bulkPrices) {
      return null;
    }

    let [cheapestPricingTier] = this.price.bulkPrices[0].tiers;
    let [cheapestBulk] = this.price.bulkPrices;

    this.price.bulkPrices.forEach(bulk => {
      bulk.tiers.forEach(tier => {
        if (tier.decimilliesAmount < cheapestPricingTier.decimilliesAmount) {
          cheapestPricingTier = tier;
          cheapestBulk = bulk;
        }
      });
    });

    this.displayServicePackageName = this.servicePackageName.transform(cheapestBulk, this.servicePackage);
    return this.formatBulkPrice(cheapestPricingTier.decimilliesAmount, this.price.currency, this.formatter);
  }

  private formatPrice(centAmount: number | null, currency: string, currencyPipe: CurrencyPipe): string | null {
    const normalizedAmount = centAmount ? centAmount / 100 : 0;
    const formatOptions = centAmount ? '1.0-2' : '1.2-2';
    return currencyPipe.transform(normalizedAmount, currency, 'symbol', formatOptions);
  }
}
